import {
  PaymentsApi,
  UserPlan,
  UserPlansResponse,
  UserPurchasesResponse,
  UserPurchases,
  PlansList,
  ProductsList,
  PlansListPlansInner,
} from '@focusrite-novation/ampify-api';
import {WindowLocation} from '@reach/router';
import {PlanIDs} from '../../../cypress/fixtures/lib/user-info';
import {isPerpetual} from '../payment';
import {Items} from '../purchases';

export interface MergedPlan extends UserPlan {
  softwareIds: string[];
}

export interface MergedPurchase extends UserPurchases {
  softwareIds: string[];
}

export const fetchUserPurchases = async (
  api: PaymentsApi
): Promise<UserPurchasesResponse> => {
  const userPurchasesResponse = await api.getUserPurchases();
  return userPurchasesResponse.data;
};

export const fetchUserPlans = async (
  api: PaymentsApi
): Promise<UserPlansResponse> => {
  const userPlansResponse = await api.getUserPlans();
  return userPlansResponse.data;
};

export const fetchPlans = async (api: PaymentsApi): Promise<MergedPlan[]> => {
  const allPlans = (await api.getPlans()).data;
  const userPlans = await fetchUserPlans(api);

  return mergeResponses(allPlans, userPlans);
};

export const fetchPurchases = async (
  api: PaymentsApi
): Promise<MergedPurchase[]> => {
  const [softProdRes, userPurchases] = await Promise.all([
    api.getSoftwareProducts(),
    fetchUserPurchases(api),
  ]);

  return mergePurchases(softProdRes.data, userPurchases);
};

export const fetchCollectivePlansMap = async (api: PaymentsApi) => {
  const plans = await fetchPlans(api);

  if (!plans) return new Map();

  const collectivePlans = plans.filter((plan) => {
    return plan?.softwareIds?.some((id) => id.includes('com.focusrite.fast-'));
  });

  const mapFormat =
    collectivePlans?.map((plan): [string, string] => {
      return [plan.name!, plan.id!];
    }) || [];

  return new Map(mapFormat);
};

type WithSoftwareIds = {
  softwareIds?: string[];
};

export const getAccessibleIds = (purchases: WithSoftwareIds[]): Set<string> => {
  return purchases.reduce((accessibleIds, purchase) => {
    purchase?.softwareIds?.forEach((id) => accessibleIds.add(id));
    return accessibleIds;
  }, new Set<string>());
};

export const mergeResponses = (
  allPlansResponse: PlansList,
  userPlansResponse: UserPlansResponse
): MergedPlan[] => {
  return (userPlansResponse.plans || []).map((userPlan) => ({
    ...userPlan,
    softwareIds:
      allPlansResponse.plans?.find((plan) => plan.id === userPlan.id)
        ?.softwareIds || [],
  }));
};

export const mergePurchases = (
  softwareProducts: ProductsList,
  userPurchases: UserPurchasesResponse
): MergedPurchase[] => {
  return (userPurchases.purchases || []).map((product) => ({
    ...product,
    softwareIds:
      softwareProducts.products?.find((p) => p.id === product.id)
        ?.softwareIds || [],
  }));
};

export interface RealUserPlan extends UserPlan {
  softwareIds?: string[];
}
export interface RealUserPurchases extends UserPurchases {
  softwareIds?: string[];
}

const buildOwnedIds = (
  bundle4Plan: PlansListPlansInner,
  userPlans: RealUserPlan[],
  userPurchases: RealUserPurchases[]
): string[] => {
  const userSoftwareIds = userPlans.flatMap(({softwareIds}) => softwareIds);
  userPurchases.forEach(({softwareIds}) =>
    userSoftwareIds.push(softwareIds![0])
  );

  const ownedIds = bundle4Plan?.softwareIds?.filter((softwareId) => {
    return userSoftwareIds?.includes(softwareId);
  });

  return ownedIds!;
};

export const isBundle4Owned = (
  plans: PlansListPlansInner[],
  userPlans: RealUserPlan[],
  userPurchases: RealUserPurchases[]
): Boolean => {
  const bundle4PlanId = PlanIDs.Bundle4;
  const bundle4ItemId = Items.FAST_BUNDLE_4;

  const bundle4Plan = plans.find(({id}) => id === bundle4PlanId);

  const userBundlePurchase = userPurchases.find(({id}) => id === bundle4ItemId);
  const userBundlePlan = userPlans.find(({id}) => id === bundle4PlanId);

  if (userBundlePurchase || userBundlePlan) {
    return true;
  }

  const ownedIds = buildOwnedIds(bundle4Plan!, userPlans, userPurchases);

  return ownedIds?.length === bundle4Plan?.softwareIds?.length;
};

export enum LimiterUpgradeCoupons {
  Perpetual = 'bundle-4-upgrade-perp',
  RTO = 'bundle-4-upgrade-rto',
}

export const getLimiterCoupon = (location: WindowLocation) => {
  return isPerpetual(location)
    ? LimiterUpgradeCoupons.Perpetual
    : LimiterUpgradeCoupons.RTO;
};
